import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';
import { startHybridPolling } from '../utils/refreshPolicy';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const isDevBypassEnabled = process.env.REACT_APP_ENABLE_DEV_BYPASS === 'true';
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  // Clear session on page load/refresh - require login every time
  useEffect(() => {
    // Clear any stored session
    localStorage.removeItem('user');

    // ONE-TIME CLEANUP: Clear all old work session data for all users
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('workState_')) {
        localStorage.removeItem(key);
        console.log(`Cleared old work data: ${key}`);
      }
    });

    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  }, []);

  // Update online status in Firebase when user logs in/out
  const updateOnlineStatus = async (userId, isOnline) => {
    try {
      await userAPI.updateOnlineStatus(userId, isOnline);
    } catch (error) {
      console.error('Error updating online status:', error);
    }
  };

  // Fetch all online users periodically
  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        const users = await userAPI.getAll();
        const online = new Set(users.filter(u => u.isOnline).map(u => u.id));
        setOnlineUsers(online);
      } catch (error) {
        console.error('Error fetching online users:', error);
      }
    };

    // Fetch immediately and then hybrid refresh (30s active, slower in background)
    if (isAuthenticated) {
      fetchOnlineUsers();
      const stop = startHybridPolling(fetchOnlineUsers, 30000);
      return () => stop();
    }
  }, [isAuthenticated]);

  // Handle page close/unload - set user offline
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handlePageClose = () => {
      // Use sendBeacon for reliable delivery during page unload
      const apiUrl = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env ? import.meta.env.REACT_APP_BACKEND_URL : null) || 'http://localhost:8001';
      const blob = new Blob([JSON.stringify({ isOnline: false })], { type: 'application/json' });
      navigator.sendBeacon(`${apiUrl}/api/users/${user.id}/online`, blob);
    };

    // Listen for page close/unload events
    window.addEventListener('beforeunload', handlePageClose);

    // Also handle visibility change (when tab is closed or browser minimized)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handlePageClose();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handlePageClose);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user]);

  const login = async (pin, attempt = 1) => {
    try {
      const userData = await authAPI.login(pin);
      setUser(userData);
      setIsAuthenticated(true);

      // Mark user as online
      await updateOnlineStatus(userData.id, true);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);

      // Optional dev bypass (disabled by default)
      if (isDevBypassEnabled && pin === '1966') {
        console.log('⚠️ ACTIVATING DEV BYPASS LOGIN');
        const mockUser = {
          id: 'dev_user',
          name: 'Dev User',
          role: 'admin',
          department: 'Development',
          avatar: '',
          isActive: true
        };
        setUser(mockUser);
        setIsAuthenticated(true);
        return { success: true, user: mockUser };
      }

      const status = error.response?.status;
      const isNetworkError = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
      // Transient Firebase/GCP hiccups (quota spikes, warmup, circuit-open) — not a wrong PIN.
      const isTransient = isNetworkError || status === 429 || status === 502 || status === 503 || status === 504;

      // Auto-retry a transient failure once before surfacing an error, so a brief
      // Firebase quota blip doesn't look like a crash or a wrong PIN.
      if (isTransient && attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return login(pin, attempt + 1);
      }

      if (status === 401) {
        return { success: false, errorType: 'invalid_pin', error: 'Invalid PIN' };
      }

      return {
        success: false,
        errorType: isTransient ? 'server_busy' : 'unknown',
        error: isTransient
          ? 'Server is temporarily busy (Firebase). Please try again in a moment.'
          : (error.response?.data?.detail || 'Something went wrong. Please try again.')
      };
    }
  };

  const logout = async () => {
    // Mark user as offline before logging out
    if (user) {
      await updateOnlineStatus(user.id, false);
    }
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
  };

  const changePin = async (oldPin, newPin) => {
    try {
      await authAPI.changePin(user.id, oldPin, newPin);
      return { success: true };
    } catch (error) {
      console.error('PIN change error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to change PIN'
      };
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updatedUserData };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, loading, login, logout, changePin, updateUser, onlineUsers }}>
      {children}
    </AuthContext.Provider>
  );
};
